import { APIComponentAttributes, APITagAttributes } from '@ardoq/api-types';
import { EnhancedScopeData } from '@ardoq/data-model';
import { FieldRenderer } from './FieldRenderer';
import styled from 'styled-components';
import { LilInfoDoq, DoqType } from '@ardoq/doq';
import { Tags } from './Tags';
import { Stack } from '@ardoq/layout';
import { PermissionContext } from '@ardoq/access-control';
import { ReactNode } from 'react';

const FieldLayout = styled(Stack).attrs({ gap: 'xsmall' })`
  width: 100%;
`;

type DefaultEmptyStateProps = {
  title?: string;
  message?: ReactNode;
};

const DefaultEmptyState = ({
  title = 'No Details',
  message = (
    <>
      <div>Looks like there are no details to show.</div>
      <div>
        If you think this is a mistake, please contact your administrator.
      </div>
    </>
  ),
}: DefaultEmptyStateProps) => (
  <LilInfoDoq doqType={DoqType.SIMPLE} message={message} title={title} />
);

type DetailsProps = {
  detailNames: string[];
  component: APIComponentAttributes;
  enhancedScopeData: EnhancedScopeData;
  tags?: APITagAttributes[];
  permissionContext: PermissionContext;
  EmptyStateComponent?: React.ReactElement;
  emptyStateTitle?: string;
  emptyStateMessage?: ReactNode;
};

const Details = ({
  detailNames,
  component,
  enhancedScopeData,
  tags,
  permissionContext,
  EmptyStateComponent,
  emptyStateTitle,
  emptyStateMessage,
}: DetailsProps) => {
  return detailNames.length ? (
    <FieldLayout>
      {detailNames.map((detailName, i) => {
        return (
          <FieldRenderer
            key={i}
            fieldName={detailName}
            component={component}
            scopeData={enhancedScopeData}
            permissionContext={permissionContext}
          />
        );
      })}
      {tags?.length ? (
        <FieldRenderer
          fieldName={'Tags'}
          component={component}
          scopeData={enhancedScopeData}
          customValueRender={<Tags tags={tags} />}
          permissionContext={permissionContext}
        />
      ) : null}
    </FieldLayout>
  ) : (
    <>
      {EmptyStateComponent ?? (
        <DefaultEmptyState
          title={emptyStateTitle}
          message={emptyStateMessage}
        />
      )}
    </>
  );
};

export { Details };
